/**
 * InstrumentMultiselect - PROM-instrument selection grid (Sprint 3.4 REDO).
 *
 * Renders the closed catalogue defined in `./types.ts` with a checkbox
 * per row. The prototype shows: MSQ, PHQ-9, SF-12, GSRS, IWQOL, DQOLY,
 * GAD-7, EQ-5D, KOOS. Each row carries the instrument code, item count,
 * and IP/free licensing tag.
 *
 * Selection state is owned by the caller (the SessionCapture view) so
 * the schedule selector + FHIR preview can re-render off the same
 * source of truth.
 *
 * HC1 reproducibility: order is fixed by the PROM_INSTRUMENTS export.
 * HC2 token-only.
 * HC5 zero em / en dashes.
 */
import { PROM_INSTRUMENTS, type PromInstrumentCode } from './types';

export interface InstrumentMultiselectProps {
  /** Currently selected instrument codes (controlled). */
  selected: PromInstrumentCode[];
  /** Toggle handler called with the new selection list. */
  onChange: (next: PromInstrumentCode[]) => void;
}

export function InstrumentMultiselect({
  selected,
  onChange,
}: InstrumentMultiselectProps) {
  const isSelected = (code: PromInstrumentCode) => selected.includes(code);

  function handleToggle(code: PromInstrumentCode) {
    if (isSelected(code)) {
      onChange(selected.filter((c) => c !== code));
    } else {
      onChange([...selected, code]);
    }
  }

  return (
    <section
      data-testid="instrument-multiselect"
      aria-labelledby="instrument-multiselect-title"
      className="rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-gutter-md">
        <h3
          id="instrument-multiselect-title"
          className="text-signal-lg font-semibold text-text-primary"
        >
          PROM Instruments
        </h3>
        <span
          data-testid="instrument-multiselect-count"
          className="text-signal-xs font-semibold uppercase tracking-wider text-text-tertiary"
        >
          {selected.length} selected
        </span>
      </header>
      <p className="mt-gutter-sm text-signal-sm text-text-secondary">
        Pick the instruments the protocol administers. Selection drives the
        FHIR preview and the publish payload.
      </p>
      <ul
        role="list"
        aria-label="PROM instruments"
        className="mt-gutter-md divide-y divide-surface-border"
      >
        {PROM_INSTRUMENTS.map((instrument) => {
          const checked = isSelected(instrument.code);
          const inputId = `prom-instrument-${instrument.code.toLowerCase()}`;
          return (
            <li
              key={instrument.code}
              data-testid={`instrument-row-${instrument.code}`}
              className="flex items-center justify-between gap-gutter-md py-gutter-sm"
            >
              <label
                htmlFor={inputId}
                className="flex flex-1 cursor-pointer items-center gap-gutter-md"
              >
                <input
                  id={inputId}
                  type="checkbox"
                  data-testid={`instrument-checkbox-${instrument.code}`}
                  checked={checked}
                  onChange={() => handleToggle(instrument.code)}
                  className="h-4 w-4 rounded-signal-sm border-surface-border text-signal-primary-500 focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
                />
                <div>
                  <p className="text-signal-sm font-semibold text-text-primary">
                    {instrument.code}
                  </p>
                  <p className="text-signal-xs text-text-secondary">
                    {instrument.name}
                  </p>
                </div>
              </label>
              <div className="flex items-center gap-gutter-md text-signal-xs text-text-tertiary">
                <span data-testid={`instrument-items-${instrument.code}`}>
                  {instrument.items} items
                </span>
                <span
                  data-testid={`instrument-licensing-${instrument.code}`}
                  className={
                    instrument.licensing === 'IP'
                      ? 'rounded-signal-pill bg-status-draft-bg px-gutter-sm py-0.5 font-semibold text-status-draft-fg'
                      : 'rounded-signal-pill bg-status-active-bg px-gutter-sm py-0.5 font-semibold text-status-active-fg'
                  }
                >
                  {instrument.licensing}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
